import { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  Mail,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, apiError, money } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

function waited(hours) {
  const h = Number(hours) || 0;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min ago`;
  if (h < 24) return `${Math.round(h)} hr ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

const Stat = ({ label, value }) => (
  <Card className="border-slate-200">
    <CardContent className="p-5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1.5 font-display text-3xl font-bold text-slate-900">{value}</div>
    </CardContent>
  </Card>
);

function DropOffRow({ row, expanded, onToggle }) {
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(row.email);
      toast.success("Email copied");
    } catch {
      toast.error("Could not copy — select it manually");
    }
  };

  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-1 py-4 text-left hover:bg-slate-50"
        data-testid={`dropoff-row-${row.family_id}`}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-slate-900">{row.parent_name}</div>
          <div className="truncate text-xs text-slate-500">
            {row.partner_name} · {row.students.length} student
            {row.students.length === 1 ? "" : "s"} · {waited(row.hours_waiting)}
          </div>
        </div>
        <Badge
          className={
            row.checkout_attempts > 0
              ? "shrink-0 bg-amber-100 text-amber-900"
              : "shrink-0 bg-slate-100 text-slate-700"
          }
        >
          {row.stage}
        </Badge>
        <div className="w-24 shrink-0 text-right font-semibold text-slate-900">
          {money(row.amount)}
        </div>
      </button>

      {expanded && (
        <div className="grid gap-5 bg-slate-50/70 px-9 py-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm text-slate-800" data-testid={`dropoff-email-${row.family_id}`}>
                {row.email || "No email on file"}
              </span>
              {row.email && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={copy}
                    data-testid={`copy-email-${row.family_id}`}
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={(e) => e.stopPropagation()}
                    asChild
                  >
                    <a
                      href={`mailto:${row.email}?subject=${encodeURIComponent(
                        "Finishing your Alabama Church School enrollment"
                      )}`}
                      data-testid={`mail-family-${row.family_id}`}
                    >
                      <Mail className="h-3.5 w-3.5" /> Email them
                    </a>
                  </Button>
                </>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {[
                ["Phone", row.phone || "—"],
                ["State", row.state || "—"],
                ["Enrollment type", row.enrollment_type === "initial" ? "New family" : "Re-enrollment"],
                ["Checkout attempts", String(row.checkout_attempts)],
                ["Payment status", row.payment_status],
                ["Last attempt", waited(row.hours_waiting)],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
                  <dd className="truncate text-slate-800">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Students on this enrollment
            </h4>
            <ul className="mt-2 space-y-1" data-testid={`dropoff-students-${row.family_id}`}>
              {row.students.length === 0 && (
                <li className="text-sm text-slate-500">No students added yet.</li>
              )}
              {row.students.map((s, i) => (
                <li key={`${s.name}-${i}`} className="text-sm text-slate-700">
                  {s.name}
                  {s.grade && <span className="ml-2 text-xs text-slate-500">{s.grade}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDropOffs() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: res } = await api.get("/admin/drop-offs");
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell
      nav={ADMIN_NAV}
      title="Drop-Offs"
      subtitle="Families who started enrolling but never paid"
      actions={
        <Button
          variant="outline"
          className="gap-2"
          onClick={load}
          disabled={busy}
          data-testid="refresh-dropoffs"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      {!data ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      ) : (
        <div className="space-y-8">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Needing follow-up" value={data.count} />
            <Stat label="Reached checkout" value={data.reached_checkout} />
            <Stat label="Fees not collected" value={money(data.amount_at_risk)} />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <h2 className="text-base font-semibold text-slate-900">
                Follow-up list ({data.count})
              </h2>
              <p className="mb-2 mt-1 text-xs text-slate-500">
                Shown once {data.grace_minutes} minutes have passed without payment. Click a family
                to see their contact details and students.
              </p>
              <div data-testid="dropoffs-list">
                {data.drop_offs.length === 0 ? (
                  <p className="py-6 text-sm text-slate-500" data-testid="dropoffs-empty">
                    Nobody has dropped off — every family who started has paid.
                  </p>
                ) : (
                  data.drop_offs.map((row) => (
                    <DropOffRow
                      key={row.family_id}
                      row={row}
                      expanded={!!expanded[row.family_id]}
                      onToggle={() =>
                        setExpanded((prev) => ({
                          ...prev,
                          [row.family_id]: !prev[row.family_id],
                        }))
                      }
                    />
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
